// Pure helper: decide whether the Select dropdown should open downward
// (default) or upward (flip) so that its options remain inside the visible
// area.
//
// Regression context — GitHub issue #83: "Floor Plan → Add Table: Shape
// dropdown options render below the visible viewport (clipped by fixed-size
// dialog)". When a Select sits near the bottom of a constrained scroll
// container (e.g. a modal body with `overflow-y-auto` and a small panel),
// the `top-full` dropdown lands outside the visible clip rect of that
// container, so the user sees a scrollbar but no options. We must consider
// the nearest scrollable ancestor's clip rect, not just the viewport — the
// modal panel may be much shorter than the viewport.

export interface DropDirectionInput {
  /** `getBoundingClientRect().top` of the Select trigger row, in pixels. */
  triggerTop: number;
  /** `getBoundingClientRect().bottom` of the Select trigger row, in pixels. */
  triggerBottom: number;
  /** Viewport height (typically `window.innerHeight`). */
  viewportHeight: number;
  /**
   * Total height the dropdown reserves below/above the trigger, including
   * any gap (mt-1 / mb-1). Defaults to 244px = max-h-60 (240px) + 4px gap.
   */
  dropdownHeight?: number;
  /**
   * Top edge (in viewport coordinates) of the nearest scrollable ancestor
   * that would clip an absolutely-positioned dropdown. Optional — when
   * omitted, only the viewport is considered.
   */
  containerTop?: number;
  /**
   * Bottom edge (in viewport coordinates) of the nearest scrollable
   * ancestor that would clip an absolutely-positioned dropdown.
   */
  containerBottom?: number;
}

export type DropDirection = 'up' | 'down';

/** Never shrink the list below this — under ~2 rows it is unusable. */
export const MIN_DROPDOWN_HEIGHT = 96;

export interface DropPlacement {
  direction: DropDirection;
  /**
   * Pixel cap for the listbox on the chosen side. When neither side can seat
   * the full dropdown, this is the usable space so the list scrolls inside the
   * clip rect instead of rendering outside it.
   */
  maxHeight: number;
}

/**
 * Space available on each side of the trigger, clamped to the nearest
 * scrollable ancestor's clip rect as well as the viewport.
 */
function availableSpace(input: DropDirectionInput): { above: number; below: number } {
  // Effective bottom edge = the closer of viewport bottom and container bottom.
  const effectiveBottom =
    input.containerBottom !== undefined
      ? Math.min(input.viewportHeight, input.containerBottom)
      : input.viewportHeight;
  // Effective top edge = the farther of viewport top (0) and container top.
  const effectiveTop =
    input.containerTop !== undefined ? Math.max(0, input.containerTop) : 0;

  return {
    above: input.triggerTop - effectiveTop,
    below: effectiveBottom - input.triggerBottom,
  };
}

export function computeSelectDropDirection(input: DropDirectionInput): DropDirection {
  const dh = input.dropdownHeight ?? 244;
  const { above, below } = availableSpace(input);
  if (below >= dh) return 'down';
  // Only flip up when the extra room above is actually usable. Comparing the
  // two sides alone would flip into a gap that is merely *less bad* than the
  // one below, leaving the list clipped on the other edge instead.
  if (above >= dh) return 'up';
  if (above > below) return 'up';
  return 'down';
}

/**
 * Direction plus the height cap to apply. When neither side fits the full
 * dropdown, the roomier side is chosen and the list is capped to that space
 * (floored at MIN_DROPDOWN_HEIGHT) so its own scrollbar takes over.
 */
export function computeSelectDropPlacement(input: DropDirectionInput): DropPlacement {
  const dh = input.dropdownHeight ?? 244;
  const direction = computeSelectDropDirection(input);
  const { above, below } = availableSpace(input);
  const space = direction === 'up' ? above : below;
  // `- 4` leaves the mt-1/mb-1 gap between trigger and list.
  const maxHeight = Math.max(MIN_DROPDOWN_HEIGHT, Math.min(dh, space - 4));
  return { direction, maxHeight };
}
